/**
 * Floating Text Format Toolbar Plugin
 *
 * Based on Lexical Playground's FloatingTextFormatToolbarPlugin with custom UI.
 * Shows a floating toolbar when text is selected.
 *
 * Custom Features (not in Lexical):
 * - Font color and background color formatting
 *
 * Features in Lexical (not implemented here):
 * - Comment Plugin (INSERT_INLINE_COMMAND) for collaborative inline comments
 * - Text case formatting: uppercase, lowercase, capitalize (FORMAT_TEXT_COMMAND)
 *   These may be useful additions in the future.
 */

import React, { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { $isCodeHighlightNode } from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  getDOMSelection,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import { getDOMRangeRect } from '../../../utils/getDOMRangeRect';
import { getSelectedNode } from '../../../utils/getSelectedNode';
import { setFloatingElemPosition } from '../../../utils/setFloatingElemPosition';

import { Box, IconButton } from '@mui/material';

import {
  Code as CodeIcon,
  FormatBoldOutlined,
  FormatItalicOutlined,
  StrikethroughSOutlined,
  FormatUnderlinedOutlined,
  InsertLinkOutlined,
  SuperscriptOutlined,
  SubscriptOutlined,
} from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { getStyleObjectFromCSS } from '../../ColorPlugin';
import FontColorFormat from '../../ToolbarPlugin/FontColorFormat';
import BackgroundColorFormat from '../../ToolbarPlugin/BgColorFormat';

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isLink,
  isBold,
  isItalic,
  isUnderline,
  isCode,
  isStrikethrough,
  isSubscript,
  isSuperscript,
  setIsLinkEditMode,
  setIsNewLink,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isCode: boolean;
  isItalic: boolean;
  isLink: boolean;
  isStrikethrough: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isUnderline: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  setIsNewLink: Dispatch<boolean>;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);
  const [fontColor, setFontColor] = useState<string>('#000000');
  const [bgColor, setBgColor] = useState<string>('#ffffff');

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsNewLink(true);
      setIsLinkEditMode(true);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      setIsLinkEditMode(false);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink, setIsLinkEditMode, setIsNewLink]);

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          // Mouse is not over the target element => not a normal click, but probably a drag
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  }
  function mouseUpListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const $updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = getDOMSelection(editor._window);

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem, isLink);
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        if ($isTextNode(anchorNode)) {
          const styleString = anchorNode.getStyle();
          const styleObject = getStyleObjectFromCSS(styleString);
          setFontColor(styleObject['color'] || '#000000');
          setBgColor(styleObject['background-color'] || '#ffffff');
        } else {
          setFontColor('#000000');
          setBgColor('#ffffff');
        }
      }
    }
  }, [editor, anchorElem, isLink]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, $updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, $updateTextFormatFloatingToolbar]);
  const IS_APPLE = false;

  return (
    <Box
      ref={popupCharStylesEditorRef}
      className='floating-text-format-popup'
      sx={{
        display: 'flex',
        backgroundColor: '#FFF',
        p: 1,
        alignItems: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        opacity: 0,
        boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.3)',
        borderRadius: 1,
        transition: 'opacity 0.5s',
        height: 36,
        willChange: 'transform',
      }}
    >
      {editor.isEditable() && (
        <>
          <IconButton
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            className='toolbar-item'
            color={isBold ? 'primary' : 'neutral'}
            title={
              IS_APPLE
                ? `${t('contentEditor.toolbar.format.bold')} (⌘B)`
                : `${t('contentEditor.toolbar.format.bold')} (Ctrl+B)`
            }
            aria-label={t('contentEditor.toolbar.format.bold')}
          >
            <FormatBoldOutlined />
          </IconButton>
          <IconButton
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            }}
            className='toolbar-item'
            color={isItalic ? 'primary' : 'neutral'}
            title={
              IS_APPLE
                ? `${t('contentEditor.toolbar.format.italic')} (⌘I)`
                : `${t('contentEditor.toolbar.format.italic')} (Ctrl+I)`
            }
            aria-label={t('contentEditor.toolbar.format.italic')}
          >
            <FormatItalicOutlined />
          </IconButton>
          <IconButton
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            }}
            className='toolbar-item'
            color={isUnderline ? 'primary' : 'neutral'}
            title={
              IS_APPLE
                ? `${t('contentEditor.toolbar.format.underline')} (⌘U)`
                : `${t('contentEditor.toolbar.format.underline')} (Ctrl+U)`
            }
            aria-label={t('contentEditor.toolbar.format.underline')}
          >
            <FormatUnderlinedOutlined />
          </IconButton>
          <IconButton
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
            }}
            className='toolbar-item'
            color={isStrikethrough ? 'primary' : 'neutral'}
            title={t('contentEditor.toolbar.format.strikethrough')}
            aria-label={t('contentEditor.toolbar.format.strikethrough')}
          >
            <StrikethroughSOutlined />
          </IconButton>
          <IconButton
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
            }}
            className='toolbar-item'
            color={isSubscript ? 'primary' : 'neutral'}
            title={t('contentEditor.toolbar.format.subscript')}
            aria-label={t('contentEditor.toolbar.format.subscript')}
          >
            <SubscriptOutlined />
          </IconButton>
          <IconButton
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
            }}
            className='toolbar-item'
            color={isSuperscript ? 'primary' : 'neutral'}
            title={t('contentEditor.toolbar.format.superscript')}
            aria-label={t('contentEditor.toolbar.format.superscript')}
          >
            <SuperscriptOutlined />
          </IconButton>
          <IconButton
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
            }}
            className='toolbar-item'
            color={isCode ? 'primary' : 'neutral'}
            title={t('contentEditor.toolbar.code')}
            aria-label={t('contentEditor.toolbar.code')}
          >
            <CodeIcon />
          </IconButton>
          <IconButton
            onClick={insertLink}
            className='toolbar-item'
            color={isLink ? 'primary' : 'neutral'}
            title={t('contentEditor.toolbar.link.format')}
            aria-label={t('contentEditor.toolbar.link.format')}
          >
            <InsertLinkOutlined />
          </IconButton>
          <FontColorFormat
            editor={editor}
            disabled={!editor.isEditable()}
            fontColor={fontColor}
          />
          <BackgroundColorFormat
            editor={editor}
            disabled={!editor.isEditable()}
            bgColor={bgColor}
          />
        </>
      )}
    </Box>
  );
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  setIsLinkEditMode: Dispatch<boolean>,
  setIsNewLink: Dispatch<boolean>
): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = getDOMSelection(editor._window);
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const node = getSelectedNode(selection);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));
      setIsCode(selection.hasFormat('code'));

      // Update links
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== '') {
        setIsText($isTextNode(node) || $isParagraphNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '');
      if (!selection.isCollapsed() && rawTextContent === '') {
        setIsText(false);
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup);
    return () => {
      document.removeEventListener('selectionchange', updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      })
    );
  }, [editor, updatePopup]);

  if (!isText) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isLink={isLink}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
      isUnderline={isUnderline}
      isCode={isCode}
      setIsLinkEditMode={setIsLinkEditMode}
      setIsNewLink={setIsNewLink}
    />,
    anchorElem
  );
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
  setIsLinkEditMode,
  setIsNewLink,
}: {
  anchorElem?: HTMLElement;
  setIsLinkEditMode: Dispatch<boolean>;
  setIsNewLink: Dispatch<boolean>;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(editor, anchorElem, setIsLinkEditMode, setIsNewLink);
}
